import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, CheckCircle2, RotateCcw, Calendar, Users } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecoveryTurniPanel = () => {
  const token = localStorage.getItem("sa_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [societaList, setSocietaList] = useState([]);
  const [societaId, setSocietaId] = useState("__all__");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/superadmin/societies-light`, { headers })
      .then((res) => { if (!cancel) setSocietaList(res.data || []); })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPayload = () => ({
    societa_id: societaId === "__all__" ? null : societaId,
    date_from: dateFrom || null,
    date_to: dateTo || null,
  });

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/recovery-turni/preview`,
        buildPayload(),
        { headers }
      );
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!preview || preview.totale === 0) return;
    const ok = window.confirm(
      `Stai per inserire ${preview.totale} turni ricostruiti dalle timbrature.\n\nConfermi?`
    );
    if (!ok) return;
    setExecuting(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/recovery-turni/execute`,
        { ...buildPayload(), confirm: true },
        { headers }
      );
      toast.success(`Recovery completato: ${res.data.inserted} turni inseriti`);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setExecuting(false);
    }
  };

  const [remapping, setRemapping] = useState(false);
  const handleRemap = async () => {
    const ok = window.confirm(
      "Stai per rimappare i turni già ricostruiti alla fascia oraria che meglio combacia. Confermi?"
    );
    if (!ok) return;
    setRemapping(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/recovery-turni/remap-fasce`,
        { societa_id: societaId === "__all__" ? null : societaId },
        { headers }
      );
      toast.success(
        `Rimappati ${res.data.rimappati}/${res.data.totale_processati} turni. ` +
        `${res.data.nessuna_fascia_trovata} senza fascia compatibile.`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setRemapping(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="recovery-turni-panel">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <RotateCcw className="h-5 w-5 text-orange-600" /> Recovery Turni da Timbrature
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Ricostruisce i turni cancellati per errore dal bug della cron del 16-17/06/2026, partendo dalle timbrature (presenze) ancora presenti nel database.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800 flex gap-3">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold">Come funziona</p>
          <ul className="mt-1 list-disc ml-4 space-y-0.5 text-xs">
            <li>Per ogni timbratura senza turno corrispondente, viene creato un turno con orari = ora entrata/uscita</li>
            <li>I turni esistenti (anche se con orari diversi) NON vengono toccati</li>
            <li>Tolleranza ±15min sull'overlap orari per evitare duplicati</li>
            <li>I turni creati hanno flag <code>ricostruito_da_timbratura=true</code> per audit</li>
            <li><strong>Fai sempre il preview prima dell'esecuzione</strong></li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-3">
        <h3 className="text-sm font-bold text-gray-900">Filtri (opzionali)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Società / Abbonamento</Label>
            <Select value={societaId} onValueChange={setSocietaId}>
              <SelectTrigger data-testid="recovery-soc-select" className="bg-white">
                <SelectValue placeholder="Tutte le società" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-80">
                <SelectItem value="__all__">Tutte le società ({societaList.length})</SelectItem>
                {societaList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Dal</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="recovery-date-from"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Al</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="recovery-date-to"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            onClick={handlePreview}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="recovery-preview-btn"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            Anteprima (dry-run)
          </Button>
          {preview && preview.totale > 0 && (
            <Button
              onClick={handleExecute}
              disabled={executing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="recovery-execute-btn"
            >
              {executing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Esegui Recovery ({preview.totale})
            </Button>
          )}
          <Button
            onClick={handleRemap}
            disabled={remapping}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="recovery-remap-btn"
          >
            {remapping ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Rimappa fasce sui turni ricostruiti
          </Button>
        </div>
      </div>

      {/* Preview results */}
      {preview && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-gray-900">Anteprima Recovery</h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-emerald-50 rounded-xl">
              <p className="text-xs text-emerald-700">Da ricostruire</p>
              <p className="text-2xl font-black text-emerald-900">{preview.totale}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500">Turni esistenti</p>
              <p className="text-2xl font-black text-gray-700">{preview.skip?.turno_esistente || 0}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl">
              <p className="text-xs text-amber-700">Dati incompleti</p>
              <p className="text-2xl font-black text-amber-900">{preview.skip?.dati_incompleti || 0}</p>
            </div>
          </div>

          {Object.keys(preview.by_mese || {}).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Distribuzione per mese
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(preview.by_mese).map(([mese, n]) => (
                  <span key={mese} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                    {mese}: <strong>{n}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {Object.keys(preview.by_utente_top || {}).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1">
                <Users className="h-3 w-3" /> Top utenti
              </p>
              <div className="space-y-1">
                {Object.entries(preview.by_utente_top).map(([nome, n]) => (
                  <div key={nome} className="flex items-center justify-between text-xs border-b border-gray-100 py-1">
                    <span className="truncate">{nome}</span>
                    <span className="font-bold">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preview.sample?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Esempio (primi 10)</p>
              <div className="text-xs font-mono space-y-1 bg-gray-50 rounded p-2 max-h-48 overflow-y-auto">
                {preview.sample.map((t, i) => (
                  <div key={i}>
                    {t.data} {t.ora_inizio}-{t.ora_fine} · <strong>{t.utente_nome}</strong> · {t.reparto}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecoveryTurniPanel;
